import { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faStop,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import { useAuth } from "../providers/authprovider";
import { useMobileMode } from "../utils/useMobile";

function pad(value) {
  return String(value).padStart(2, "0");
}

function formatSeconds(totalSeconds) {
  const safeSeconds = Math.max(0, totalSeconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function getDatePart(dateValue) {
  if (!dateValue) return null;

  if (typeof dateValue === "string") {
    return dateValue.slice(0, 10);
  }

  return null;
}

function getStartDateFromRecord(record) {
  if (!record?.date || !record?.start_time) return null;

  const datePart = getDatePart(record.date);
  if (!datePart) return null;

  return new Date(`${datePart}T${record.start_time}`);
}

export default function WorkButton(props) {
  const mobile = useMobileMode();
  const { workTime, workTimeRecord, setWorkTimeRecord } = useAuth();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const activeWork = useMemo(() => {
    if (!workTimeRecord) return null;
    if (!workTimeRecord.start_time) return null;
    if (workTimeRecord.end_time) return null;
    return workTimeRecord;
  }, [workTimeRecord]);

  useEffect(() => {
    if (!activeWork) {
      setElapsedSeconds(0);
      return;
    }

    const updateTimer = () => {
      const startDate = getStartDateFromRecord(activeWork);

      if (!startDate || Number.isNaN(startDate.getTime())) {
        setElapsedSeconds(0);
        return;
      }

      const diff = Math.floor((Date.now() - startDate.getTime()) / 1000);
      setElapsedSeconds(Math.max(0, diff));
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeWork]);

  const handleStartWork = async () => {
    if (loading) return;

    setLoading(true);
    const result = await workTime.startWork();
    setLoading(false);

    if (!result?.success) {
      console.error(result?.error || "Nie udało się rozpocząć pracy");
      return;
    }

    setWorkTimeRecord(result.content);
  };

  const handleOpenEndConfirm = () => {
    if (loading) return;
    setShowEndConfirm(true);
  };

  const handleCloseEndConfirm = () => {
    if (loading) return;
    setShowEndConfirm(false);
  };

  const handleConfirmEndWork = async () => {
    if (loading) return;

    setLoading(true);
    const result = await workTime.endWork();
    setLoading(false);

    if (!result?.success) {
      console.error(result?.error || "Nie udało się zakończyć pracy");
      return;
    }

    setWorkTimeRecord(result.content);
    setShowEndConfirm(false);
  };

  if (activeWork) {
    return (
      <>
        <Button
          variant="outline-danger"
          onClick={handleOpenEndConfirm}
          disabled={loading}
        >
          <FontAwesomeIcon icon={loading ? faSpinner : faStop} spin={loading} />{" "}
          {formatSeconds(elapsedSeconds)}
        </Button>

        <Modal
          show={showEndConfirm}
          onHide={handleCloseEndConfirm}
          centered
        >
          <Modal.Header closeButton={!loading}>
            <Modal.Title>Zakończyć pracę?</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            Czy na pewno chcesz zakończyć czas pracy?
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseEndConfirm}
              disabled={loading}
            >
              Anuluj
            </Button>
            <Button
              variant="dark"
              onClick={handleConfirmEndWork}
              disabled={loading}
            >
              <FontAwesomeIcon
                icon={loading ? faSpinner : faStop}
                spin={loading}
              />{" "}
              Zakończ pracę
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <Button
      variant="outline-success"
      onClick={handleStartWork}
      disabled={loading}
    >
      <FontAwesomeIcon icon={loading ? faSpinner : faPlay} spin={loading} />{" "}
      {mobile ? "Praca" : "Rozpocznij pracę"}
    </Button>
  );
}